import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { DatePickerField } from './DatePickerField';
import { PublicLayout } from './PublicLayout';
import { trackerApi, type SurebetHistoryApiItem } from './api';
import { TRACKER_MODE } from './services/trackerMode';
import './pages.css';

type HistoryRangePreset = 'this-month' | 'last-month' | 'last-30' | 'custom';
type HistoryTypeFilter = 'all' | 'Live' | 'Pre-match';
type HistoryLoadState = 'idle' | 'loading' | 'refreshing' | 'loaded' | 'error';

type HistoryItem = {
  id: string;
  createdAt: string;
  date: string;
  eventName: string;
  sport: string;
  competition?: string | null;
  market: string;
  selection?: string | null;
  line?: number | null;
  type: 'Live' | 'Pre-match';
  bookmakerA: string;
  directionA: string;
  oddA: number;
  suggestedStakeA: number;
  bookmakerB: string;
  directionB: string;
  oddB: number;
  suggestedStakeB: number;
  bookmakerC?: string | null;
  directionC?: string | null;
  oddC?: number | null;
  suggestedStakeC?: number | null;
  totalStake: number;
  minimumProfit: number;
  roiPercent: number;
};

const AMSTERDAM_TZ = 'Europe/Amsterdam';
const PAGE_SIZE = 20;
const MAX_VISIBLE_HISTORY_PAGE_BUTTONS = 10;

const percentFormatter = new Intl.NumberFormat('nl-NL', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const oddFormatter = new Intl.NumberFormat('nl-NL', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 3,
});

const dateFormatter = new Intl.DateTimeFormat('nl-NL', {
  timeZone: AMSTERDAM_TZ,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const dateTimeFormatter = new Intl.DateTimeFormat('nl-NL', {
  timeZone: AMSTERDAM_TZ,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const monthYearFormatter = new Intl.DateTimeFormat('nl-NL', {
  timeZone: AMSTERDAM_TZ,
  month: 'long',
  year: 'numeric',
});

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(Math.round((value + Number.EPSILON) * 100) / 100);

const formatPercent = (value: number): string => `${percentFormatter.format(value)}%`;
const formatOdd = (value: number): string => oddFormatter.format(value);

const BOOKMAKER_LABELS: Record<string, string> = {
  'betmgm-nl': 'Kambi',
};

const DIRECTION_LABELS: Record<string, string> = {
  over: 'Over',
  under: 'Under',
  yes: 'Yes',
  no: 'No',
  home: 'Home',
  away: 'Away',
  draw: 'Draw',
  both: 'Both',
};

const toNumber = (value: unknown): number => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatBookmakerLabel = (value?: string | null): string => {
  const text = String(value || '').trim();
  if (!text) return '-';
  return BOOKMAKER_LABELS[text.toLowerCase()] || text;
};

const formatDirectionLabel = (value?: string | null): string => {
  const text = String(value || '').trim();
  if (!text) return '-';
  const normalized = text.toLowerCase();

  if (normalized.startsWith('over')) return 'Over';
  if (normalized.startsWith('under')) return 'Under';

  return DIRECTION_LABELS[normalized] || text;
};

const toDateOnly = (value?: string | null): string => {
  if (!value) return '';
  return value.slice(0, 10);
};

const pad = (value: number): string => String(value).padStart(2, '0');

const buildIsoDate = (year: number, month: number, day: number): string => `${year}-${pad(month)}-${pad(day)}`;

const createStableDateFromIso = (isoDate: string): Date => {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
};

const getAmsterdamDateParts = (value = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: AMSTERDAM_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(value);

  const year = Number(parts.find((part) => part.type === 'year')?.value || '0');
  const month = Number(parts.find((part) => part.type === 'month')?.value || '1');
  const day = Number(parts.find((part) => part.type === 'day')?.value || '1');

  return {
    year,
    month,
    day,
    isoDate: buildIsoDate(year, month, day),
  };
};

const toIsoDateFromUtcDate = (value: Date): string => buildIsoDate(
  value.getUTCFullYear(),
  value.getUTCMonth() + 1,
  value.getUTCDate(),
);

const shiftIsoDateByDays = (isoDate: string, dayDelta: number): string => {
  const value = createStableDateFromIso(isoDate);
  value.setUTCDate(value.getUTCDate() + dayDelta);
  return toIsoDateFromUtcDate(value);
};

const getInclusiveDayCount = (startDate: string, endDate: string): number => {
  if (!startDate || !endDate) return 0;

  const startValue = createStableDateFromIso(startDate);
  const endValue = createStableDateFromIso(endDate);

  if (Number.isNaN(startValue.getTime()) || Number.isNaN(endValue.getTime()) || endValue.getTime() < startValue.getTime()) {
    return 0;
  }

  return Math.floor((endValue.getTime() - startValue.getTime()) / 86400000) + 1;
};

const getLatestPublishedArchiveDate = (now = new Date()): string => {
  const currentAmsterdamDate = getAmsterdamDateParts(now).isoDate;
  return shiftIsoDateByDays(currentAmsterdamDate, -1);
};

const getRangeForPreset = (preset: HistoryRangePreset, now = new Date()): { startDate: string; endDate: string } => {
  const current = getAmsterdamDateParts(now);
  const latestPublishedArchiveDate = getLatestPublishedArchiveDate(now);

  if (preset === 'this-month') {
    return {
      startDate: buildIsoDate(current.year, current.month, 1),
      endDate: latestPublishedArchiveDate,
    };
  }

  if (preset === 'last-month') {
    const previousMonth = current.month === 1 ? 12 : current.month - 1;
    const previousYear = current.month === 1 ? current.year - 1 : current.year;
    const lastDay = new Date(Date.UTC(previousYear, previousMonth, 0, 12, 0, 0)).getUTCDate();
    return {
      startDate: buildIsoDate(previousYear, previousMonth, 1),
      endDate: buildIsoDate(previousYear, previousMonth, lastDay),
    };
  }

  const endDate = createStableDateFromIso(latestPublishedArchiveDate);
  endDate.setUTCDate(endDate.getUTCDate() - 29);
  return {
    startDate: toIsoDateFromUtcDate(endDate),
    endDate: latestPublishedArchiveDate,
  };
};

const getCurrentAmsterdamDayKey = (): string => getAmsterdamDateParts().isoDate;

const formatPeriodLabel = (startDate: string, endDate: string): string => {
  if (!startDate || !endDate) return 'Geselecteerde periode';
  return `${dateFormatter.format(createStableDateFromIso(startDate))} t/m ${dateFormatter.format(createStableDateFromIso(endDate))}`;
};

const formatPeriodMonthLabel = (startDate: string, endDate: string): string => {
  if (!startDate || !endDate) return '';

  const startValue = createStableDateFromIso(startDate);
  const endValue = createStableDateFromIso(endDate);
  const startLabel = monthYearFormatter.format(startValue);
  const endLabel = monthYearFormatter.format(endValue);
  const periodLabel = startLabel === endLabel ? startLabel : `${startLabel} t/m ${endLabel}`;

  return periodLabel.charAt(0).toUpperCase() + periodLabel.slice(1);
};

const formatDateTimeAmsterdam = (value?: string | null): string => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return dateTimeFormatter.format(parsed);
};

const formatMarketLine = (value?: number | null): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '';
  return new Intl.NumberFormat('nl-NL', {
    maximumFractionDigits: 2,
    minimumFractionDigits: Number.isInteger(value) ? 0 : 1,
  }).format(value);
};

const formatHistoryMarket = (item: Pick<HistoryItem, 'market' | 'selection' | 'line'>): string => {
  const market = item.market.trim();
  const selection = item.selection?.trim();
  const line = formatMarketLine(item.line);
  const marketKey = market.toLowerCase();

  if (marketKey.includes('shot') && selection && line) {
    const metric = marketKey.includes('target') || marketKey.includes('op doel')
      ? 'Shots on Target Over/Under'
      : 'Shots Over/Under';
    return `${selection} ${line} ${metric}`;
  }

  if (selection && line) {
    return `${market} · ${selection} ${line}`;
  }
  if (selection) {
    return `${market} · ${selection}`;
  }
  if (line) {
    return `${market} · ${line}`;
  }
  return market;
};

const formatCompetitionLabel = (value?: string | null): string => {
  const text = String(value || '').trim();
  if (!text) return '';
  const parts = text.split('||').map((part) => part.trim()).filter(Boolean);
  return parts[0] || text;
};

const mapApiHistoryItem = (item: SurebetHistoryApiItem): HistoryItem => ({
  id: String(item.id),
  createdAt: String(item.createdAt || item.date || ''),
  date: toDateOnly(item.date),
  eventName: item.eventName,
  sport: item.sport,
  competition: item.competition ?? null,
  market: item.market,
  selection: item.selection ?? null,
  line: item.line ?? null,
  type: item.type === 'Live' ? 'Live' : 'Pre-match',
  bookmakerA: item.bookmakerA,
  directionA: item.directionA,
  oddA: toNumber(item.oddA),
  suggestedStakeA: toNumber(item.suggestedStakeA),
  bookmakerB: item.bookmakerB,
  directionB: item.directionB,
  oddB: toNumber(item.oddB),
  suggestedStakeB: toNumber(item.suggestedStakeB),
  bookmakerC: item.bookmakerC ?? null,
  directionC: item.directionC ?? null,
  oddC: item.oddC == null ? null : toNumber(item.oddC),
  suggestedStakeC: item.suggestedStakeC == null ? null : toNumber(item.suggestedStakeC),
  totalStake: toNumber(item.totalStake),
  minimumProfit: toNumber(item.minimumProfit),
  roiPercent: toNumber(item.roiPercent),
});

const getVisiblePageNumbers = (currentPage: number, totalPages: number): number[] => {
  if (totalPages <= MAX_VISIBLE_HISTORY_PAGE_BUTTONS) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const start = Math.max(1, currentPage - 4);
  const end = Math.min(totalPages, start + MAX_VISIBLE_HISTORY_PAGE_BUTTONS - 1);
  const adjustedStart = Math.max(1, end - MAX_VISIBLE_HISTORY_PAGE_BUTTONS + 1);

  return Array.from({ length: end - adjustedStart + 1 }, (_, index) => adjustedStart + index);
};

export const TrackerHistoryPage: React.FC = () => {
  const initialRange = getRangeForPreset('this-month');
  const [rangePreset, setRangePreset] = useState<HistoryRangePreset>('this-month');
  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);
  const [typeFilter, setTypeFilter] = useState<HistoryTypeFilter>('all');
  const [sportFilter, setSportFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [loadState, setLoadState] = useState<HistoryLoadState>('idle');
  const [loadError, setLoadError] = useState('');
  const [lastLoadedAt, setLastLoadedAt] = useState<string>('');
  const searchQuery = useDeferredValue(search.trim().toLowerCase());
  const latestRequestRef = useRef(0);
  const currentAmsterdamDayRef = useRef(getCurrentAmsterdamDayKey());
  const latestPublishedArchiveDate = getLatestPublishedArchiveDate();

  const loadHistory = useCallback(async (rangeStart: string, rangeEnd: string) => {
    latestRequestRef.current += 1;
    const requestId = latestRequestRef.current;
    setLoadError('');
    setLoadState((current) => (current === 'loaded' ? 'refreshing' : 'loading'));

    try {
      const response = await trackerApi.getTrackerHistory({
        limit: 5000,
        minRoi: 0,
        startDate: rangeStart,
        endDate: rangeEnd,
        includeCurrentDay: false,
      });

      if (requestId !== latestRequestRef.current) {
        return;
      }

      const nextItems = Array.isArray(response.data)
        ? response.data.map((item) => mapApiHistoryItem(item))
        : [];

      setItems(nextItems);
      setLastLoadedAt(new Date().toISOString());
      setLoadState('loaded');
    } catch (error) {
      if (requestId !== latestRequestRef.current) {
        return;
      }

      setItems([]);
      setLoadState('error');
      setLoadError(error instanceof Error ? error.message : 'Het surebetarchief kon niet worden geladen.');
    }
  }, []);

  useEffect(() => {
    if (!startDate || !endDate) return;
    if (startDate > endDate) {
      setItems([]);
      setLoadError('');
      setLoadState('loaded');
      setLastLoadedAt(new Date().toISOString());
      return;
    }
    void loadHistory(startDate, endDate);
  }, [endDate, loadHistory, startDate]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const nextDay = getCurrentAmsterdamDayKey();
      if (nextDay === currentAmsterdamDayRef.current) {
        return;
      }

      currentAmsterdamDayRef.current = nextDay;

      if (rangePreset === 'custom') {
        void loadHistory(startDate, endDate);
        return;
      }

      const nextRange = getRangeForPreset(rangePreset);
      setStartDate(nextRange.startDate);
      setEndDate(nextRange.endDate);
    }, 60000);

    return () => window.clearInterval(timer);
  }, [endDate, loadHistory, rangePreset, startDate]);

  const sports = useMemo(() => {
    const values = Array.from(new Set(items.map((item) => item.sport).filter(Boolean)));
    return values.sort((left, right) => left.localeCompare(right, 'nl-NL'));
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (typeFilter !== 'all' && item.type !== typeFilter) return false;
      if (sportFilter !== 'all' && item.sport !== sportFilter) return false;
      if (!searchQuery) return true;

      const haystack = [
        item.eventName,
        item.competition,
        item.market,
        item.selection,
        item.bookmakerA,
        item.directionA,
        item.bookmakerB,
        item.directionB,
        item.bookmakerC,
        item.directionC,
        item.type,
        item.sport,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(searchQuery);
    });
  }, [items, searchQuery, sportFilter, typeFilter]);

  const pageCount = useMemo(() => Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE)), [filteredItems.length]);

  useEffect(() => {
    setPage(1);
  }, [endDate, searchQuery, sportFilter, startDate, typeFilter]);

  useEffect(() => {
    if (page > pageCount) {
      setPage(pageCount);
    }
  }, [page, pageCount]);

  const pagedItems = useMemo(() => {
    const startIndex = (page - 1) * PAGE_SIZE;
    return filteredItems.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredItems, page]);

  const summary = useMemo(() => {
    const totalStake = filteredItems.reduce((sum, item) => sum + item.totalStake, 0);
    const totalProfit = filteredItems.reduce((sum, item) => sum + item.minimumProfit, 0);
    const averageProfit = filteredItems.length > 0 ? totalProfit / filteredItems.length : 0;
    const averageRoi = filteredItems.length > 0
      ? filteredItems.reduce((sum, item) => sum + item.roiPercent, 0) / filteredItems.length
      : 0;
    const activeDays = new Set(filteredItems.map((item) => item.date).filter(Boolean)).size;
    const averageProfitPerActiveDay = activeDays > 0 ? totalProfit / activeDays : 0;
    const periodDayCount = getInclusiveDayCount(startDate, endDate);
    const averageProfitPerPeriodDay = periodDayCount > 0 ? totalProfit / periodDayCount : 0;

    return {
      count: filteredItems.length,
      totalStake,
      totalProfit,
      averageRoi,
      averageProfit,
      averageProfitPerActiveDay,
      averageProfitPerPeriodDay,
      activeDays,
      periodDayCount,
    };
  }, [endDate, filteredItems, startDate]);

  const periodLabel = useMemo(() => formatPeriodLabel(startDate, endDate), [endDate, startDate]);
  const periodMonthLabel = useMemo(() => formatPeriodMonthLabel(startDate, endDate), [endDate, startDate]);
  const visiblePages = useMemo(() => getVisiblePageNumbers(page, pageCount), [page, pageCount]);
  const isHistoryLoading = loadState === 'loading' || loadState === 'refreshing';

  const handlePresetSelect = (preset: HistoryRangePreset) => {
    setRangePreset(preset);
    if (preset === 'custom') {
      return;
    }

    const nextRange = getRangeForPreset(preset);
    setStartDate(nextRange.startDate);
    setEndDate(nextRange.endDate);
  };

  return (
    <PublicLayout>
      <section className="public-page-card subscription-page tracker-page tracker-page-modern tracker-history-page-modern public-refresh archive-refresh">
        <section className="tracker-main-header card">
          <div>
            <span className="subscription-badge">Resultaten</span>
            <h1>Surebet archief</h1>
            <p>Doorzoek het volledige surebetarchief, filter op markt of periode en bekijk wat de geadviseerde surebets in jouw gekozen periode minimaal hadden opgeleverd.</p>
          </div>
          <div className="tracker-main-header-tools">
            <Link to="/tracker" className="card-btn card-link tracker-header-link">Terug naar tracker</Link>
            <span className="tracker-env-badge">
              {process.env.NODE_ENV !== 'production' ? `Tracker mode: ${TRACKER_MODE.toUpperCase()}` : 'Dagelijkse update 00:00 · Nederlandse tijd'}
            </span>
          </div>
        </section>

        <div className="tracker-history-content">
          <aside className="tracker-history-season-note" role="note">
            <span>Zomerstop</span>
            <div>
              <strong>De surebetscanner staat sinds 22 juli 2026 tijdelijk uit.</strong>
              <p>
                Tijdens de zomerstop zijn er weinig wedstrijden. Daarom worden er vanaf die datum tijdelijk geen
                nieuwe surebets gevonden of aan het archief toegevoegd.
              </p>
            </div>
          </aside>

          <div className="tracker-stat-grid tracker-history-stat-grid">
            <article className="tracker-stat-card card">
              <h3>Surebets in periode</h3>
              <p>{summary.count}</p>
              <span>{periodLabel}</span>
              {periodMonthLabel && <strong className="tracker-history-period-month">{periodMonthLabel}</strong>}
            </article>
            <article className="tracker-stat-card card">
              <h3>Gemiddelde winst per dag in deze periode</h3>
              <p>{formatCurrency(summary.averageProfitPerPeriodDay)}</p>
              <span>{summary.periodDayCount > 0 ? `Gebaseerd op ${summary.periodDayCount} kalenderdagen` : 'Kies een geldige periode'}</span>
            </article>
            <article className="tracker-stat-card card tracker-history-profit-card">
              <h3>Mogelijke winst</h3>
              <p className="tracker-history-profit-value">{formatCurrency(summary.totalProfit)}</p>
              <span>Minimale winst bij alle surebets samen</span>
            </article>
            <article className="tracker-stat-card card tracker-history-roi-card">
              <h3>Gemiddelde ROI</h3>
              <p className="tracker-history-roi-value">{formatPercent(summary.averageRoi)}</p>
              <span>Gemiddelde ROI van alle surebets in deze selectie</span>
            </article>
          </div>

          <article className="card tracker-history-sim-card">
            <div>
              <h2>Wat als je alles had gespeeld?</h2>
              <p>
                Als je alle <strong>{summary.count}</strong> surebets in <strong>{periodLabel}</strong> had geplaatst met de geadviseerde stakes,
                dan was je minimale resultaat <strong>{formatCurrency(summary.totalProfit)}</strong> geweest op een totale inzet van <strong>{formatCurrency(summary.totalStake)}</strong>.
              </p>
            </div>
            <div className="tracker-history-sim-metrics">
              <p className="tracker-history-sim-metric"><span>Gemiddeld per surebet</span><strong>{formatCurrency(summary.averageProfit)}</strong></p>
              <p className="tracker-history-sim-metric"><span>Gemiddeld per dag in periode</span><strong>{formatCurrency(summary.averageProfitPerPeriodDay)}</strong></p>
              <p className="tracker-history-sim-metric"><span>Dagen met surebets</span><strong>{summary.activeDays}</strong></p>
            </div>
          </article>

          <article className="card tracker-history-toolbar">
            <div className="tracker-history-toolbar-header">
              <div>
                <h2>Filter en doorzoek</h2>
                <p>Standaard staat de pagina op deze maand, tot en met de laatst afgesloten Nederlandse dag. Nieuwe surebets van vandaag verschijnen na 00:00 Nederlandse tijd.</p>
              </div>
              <button
                type="button"
                className="tracker-page-btn tracker-history-refresh-btn"
                onClick={() => void loadHistory(startDate, endDate)}
                disabled={isHistoryLoading}
                aria-busy={isHistoryLoading}
              >
                {isHistoryLoading ? 'Verversen...' : 'Ververs nu'}
              </button>
            </div>

            <div className="tracker-pick-filterbar" role="tablist" aria-label="History periode filter">
              <button
                type="button"
                className={`tracker-mini-pill ${rangePreset === 'this-month' ? 'tracker-mini-pill-active' : ''}`}
                onClick={() => handlePresetSelect('this-month')}
              >
                Deze maand
              </button>
              <button
                type="button"
                className={`tracker-mini-pill ${rangePreset === 'last-month' ? 'tracker-mini-pill-active' : ''}`}
                onClick={() => handlePresetSelect('last-month')}
              >
                Vorige maand
              </button>
              <button
                type="button"
                className={`tracker-mini-pill ${rangePreset === 'last-30' ? 'tracker-mini-pill-active' : ''}`}
                onClick={() => handlePresetSelect('last-30')}
              >
                Laatste 30 dagen
              </button>
              <button
                type="button"
                className={`tracker-mini-pill ${rangePreset === 'custom' ? 'tracker-mini-pill-active' : ''}`}
                onClick={() => handlePresetSelect('custom')}
              >
                Aangepast
              </button>
            </div>

            <div className="tracker-history-filter-grid">
              <div className="tracker-field tracker-history-search-field">
                <label htmlFor="tracker-history-search">Zoek surebet</label>
                <input
                  id="tracker-history-search"
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Team, speler, markt, competitie of bookmaker"
                />
              </div>
              <div className="tracker-field">
                <label htmlFor="tracker-history-type">Type</label>
                <select id="tracker-history-type" value={typeFilter} onChange={(event) => setTypeFilter(event.target.value as HistoryTypeFilter)}>
                  <option value="all">Live en pre-match</option>
                  <option value="Pre-match">Pre-match</option>
                  <option value="Live">Live</option>
                </select>
              </div>
              <div className="tracker-field">
                <label htmlFor="tracker-history-sport">Sport</label>
                <select id="tracker-history-sport" value={sportFilter} onChange={(event) => setSportFilter(event.target.value)}>
                  <option value="all">Alle sporten</option>
                  {sports.map((sport) => (
                    <option key={sport} value={sport}>{sport}</option>
                  ))}
                </select>
              </div>
              <DatePickerField
                id="tracker-history-start"
                label="Van"
                value={startDate}
                max={latestPublishedArchiveDate}
                onChange={(nextDate) => {
                    setRangePreset('custom');
                    setStartDate(nextDate);
                  }}
              />
              <DatePickerField
                id="tracker-history-end"
                label="Tot en met"
                value={endDate}
                max={latestPublishedArchiveDate}
                onChange={(nextDate) => {
                    setRangePreset('custom');
                    setEndDate(nextDate);
                  }}
              />
            </div>

            <p className="tracker-history-update-note">
              Laatst bijgewerkt: <strong>{lastLoadedAt ? formatDateTimeAmsterdam(lastLoadedAt) : 'nog niet geladen'}</strong>. Het openbare archief toont surebets tot en met <strong>{dateFormatter.format(createStableDateFromIso(latestPublishedArchiveDate))}</strong>.
            </p>
          </article>

          {loadState === 'loading' && items.length === 0 && (
            <article className="card tracker-history-state-card">
              <div className="site-loading-block" role="status" aria-live="polite">
                <span className="site-loading-spinner" aria-hidden="true" />
                <div className="site-loading-copy">
                  <h3>Surebet archief laden...</h3>
                  <p>De geselecteerde periode wordt opgehaald.</p>
                </div>
              </div>
            </article>
          )}

          {loadState === 'error' && (
            <article className="card tracker-history-state-card tracker-history-state-card-error">
              <h3>Surebet archief kon niet worden geladen</h3>
              <p>{loadError || 'Probeer het opnieuw of ververs de pagina.'}</p>
              <button type="button" className="card-btn tracker-history-retry-btn" onClick={() => void loadHistory(startDate, endDate)}>Opnieuw proberen</button>
            </article>
          )}

          {loadState !== 'loading' && loadState !== 'error' && (
            <article className="card tracker-history-table-card">
              <div className="tracker-section-header tracker-history-list-header">
                <div>
                  <h2>Surebets in {periodLabel}</h2>
                  <p>{filteredItems.length} resultaten gevonden. Pagina {page} van {pageCount}.</p>
                </div>
              </div>

              {filteredItems.length === 0 ? (
                <div className="tracker-empty-state-modern tracker-history-empty-state">
                  <h4>Geen surebets gevonden</h4>
                  <p>Pas je periode of filters aan om resultaten te zien.</p>
                </div>
              ) : (
                <>
                  <div className="tracker-history-table-shell">
                    <table className="tracker-history-table">
                      <thead>
                        <tr>
                          <th>Datum</th>
                          <th>Surebet</th>
                          <th>Type</th>
                          <th>Odd A</th>
                          <th>Odd B</th>
                          <th>ROI%</th>
                          <th>Mogelijke winst</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedItems.map((item) => (
                          <tr key={item.id}>
                            <td>
                              <div className="tracker-history-date-cell">
                                <strong>{item.date ? dateFormatter.format(createStableDateFromIso(item.date)) : '-'}</strong>
                                <span>{formatDateTimeAmsterdam(item.createdAt)}</span>
                              </div>
                            </td>
                            <td>
                              <div className="tracker-history-surebet-cell">
                                <strong>{item.eventName}</strong>
                                <span>{formatHistoryMarket(item)}</span>
                                <small>
                                  {formatBookmakerLabel(item.bookmakerA)} vs {formatBookmakerLabel(item.bookmakerB)}
                                  {item.competition ? ` · ${formatCompetitionLabel(item.competition)}` : ''}
                                </small>
                              </div>
                            </td>
                            <td>
                              <span className={`tracker-history-type-badge is-${item.type.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{item.type}</span>
                            </td>
                            <td>
                              <div className="tracker-history-odd-cell">
                                <span>{formatBookmakerLabel(item.bookmakerA)}</span>
                                <strong>{formatOdd(item.oddA)}</strong>
                                <small>{formatDirectionLabel(item.directionA)}</small>
                              </div>
                            </td>
                            <td>
                              <div className="tracker-history-odd-cell">
                                <span>{formatBookmakerLabel(item.bookmakerB)}</span>
                                <strong>{formatOdd(item.oddB)}</strong>
                                <small>{formatDirectionLabel(item.directionB)}</small>
                              </div>
                            </td>
                            <td>
                              <strong className="tracker-history-roi">{formatPercent(item.roiPercent)}</strong>
                            </td>
                            <td>
                              <div className="tracker-history-profit-cell">
                                <strong>{formatCurrency(item.minimumProfit)}</strong>
                                <span>bij {formatCurrency(item.totalStake)} inzet</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="tracker-history-mobile-list">
                    {pagedItems.map((item) => (
                      <article key={item.id} className="tracker-history-mobile-card">
                        <div className="tracker-history-mobile-topline">
                          <strong>{item.eventName}</strong>
                          <span>{formatPercent(item.roiPercent)}</span>
                        </div>
                        <p>{formatHistoryMarket(item)}</p>
                        <p>{item.date ? dateFormatter.format(createStableDateFromIso(item.date)) : '-'} · {item.type} · {item.sport}</p>
                        <div className="tracker-history-mobile-odds">
                          <div className="tracker-history-mobile-odd-item">
                            <span>{formatBookmakerLabel(item.bookmakerA)}</span>
                            <strong>{formatOdd(item.oddA)}</strong>
                            <small>{formatDirectionLabel(item.directionA)}</small>
                          </div>
                          <div className="tracker-history-mobile-odd-item">
                            <span>{formatBookmakerLabel(item.bookmakerB)}</span>
                            <strong>{formatOdd(item.oddB)}</strong>
                            <small>{formatDirectionLabel(item.directionB)}</small>
                          </div>
                        </div>
                        <p className="tracker-history-mobile-profit">Mogelijke winst: <strong>{formatCurrency(item.minimumProfit)}</strong> bij {formatCurrency(item.totalStake)} inzet</p>
                      </article>
                    ))}
                  </div>

                  {pageCount > 1 && (
                    <div className="tracker-pagination" aria-label="Surebet archief paginering">
                      <button
                        type="button"
                        className="tracker-page-btn"
                        onClick={() => setPage(1)}
                        disabled={page === 1}
                      >
                        Eerste
                      </button>
                      <button
                        type="button"
                        className="tracker-page-btn"
                        onClick={() => setPage((current) => Math.max(1, current - 1))}
                        disabled={page === 1}
                      >
                        Vorige
                      </button>
                      <div className="tracker-page-list">
                        {visiblePages.map((pageNumber) => (
                          <button
                            key={pageNumber}
                            type="button"
                            className={`tracker-page-btn ${page === pageNumber ? 'tracker-page-btn-active' : ''}`}
                            onClick={() => setPage(pageNumber)}
                            aria-current={page === pageNumber ? 'page' : undefined}
                          >
                            {pageNumber}
                          </button>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="tracker-page-btn"
                        onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
                        disabled={page === pageCount}
                      >
                        Volgende
                      </button>
                      <button
                        type="button"
                        className="tracker-page-btn"
                        onClick={() => setPage(pageCount)}
                        disabled={page === pageCount}
                      >
                        Laatste
                      </button>
                    </div>
                  )}
                </>
              )}
            </article>
          )}
        </div>
      </section>
    </PublicLayout>
  );
};

export default TrackerHistoryPage;
